/**
 * The colour ramps, as the frontend sees them. Section 9.
 *
 * There is exactly one definition of every ramp and hazard colour:
 * backend/render/colormaps.py. This module holds no colour literal. It loads
 * that definition from GET /api/colormaps once, before the app's first render
 * (main.tsx awaits `loadColormaps`), and fills the exports below in place, so
 * the legend swatch beside "2 m" is the colour the overlay PNG, the PDF and the
 * KML use at 2 m.
 */

import { API_BASE } from '../api/client'

export type RampStop = [number, string]

interface ColormapsWire {
  ramps: Record<string, RampStop[]>
  above_top: Record<string, string>
  hazard: Record<string, string>
  hazard_labels: Record<string, string>
  units: Record<string, string>
  water_3d: { ramp: RampStop[]; above_top: string }
}

export const HAZARD: Record<number, string> = {}
export const HAZARD_LABELS: Record<number, string> = {}
export const RAMPS: Record<string, RampStop[]> = {}
export const ABOVE_TOP: Record<string, string> = {}
export const UNITS: Record<string, string> = {}
export const DEPTH_RAMP: RampStop[] = []
/** The 3-D view's water surface: the depth breaks of DEPTH_RAMP in water colours. */
export const WATER_3D_RAMP: RampStop[] = []
export const WATER_3D: { aboveTop: string } = { aboveTop: '' }
export const VELOCITY_RAMP: RampStop[] = []
export const ARRIVAL_RAMP: RampStop[] = []
export const DURATION_RAMP: RampStop[] = []

let loading: Promise<void> | null = null

/** Fetch the one colour definition and fill every export in place. Idempotent. */
export function loadColormaps(): Promise<void> {
  loading ??= fetch(`${API_BASE}/colormaps`)
    .then((r) => {
      if (!r.ok) throw new Error(`GET /colormaps: HTTP ${r.status}`)
      return r.json() as Promise<ColormapsWire>
    })
    .then((wire) => {
      for (const [k, v] of Object.entries(wire.hazard)) HAZARD[Number(k)] = v
      for (const [k, v] of Object.entries(wire.hazard_labels)) HAZARD_LABELS[Number(k)] = v
      Object.assign(RAMPS, wire.ramps)
      Object.assign(ABOVE_TOP, wire.above_top)
      Object.assign(UNITS, wire.units)
      const fill = (target: RampStop[], layer: string) => target.splice(0, target.length, ...(wire.ramps[layer] ?? []))
      fill(DEPTH_RAMP, 'depth')
      fill(VELOCITY_RAMP, 'velocity')
      fill(ARRIVAL_RAMP, 'arrival')
      fill(DURATION_RAMP, 'duration')
      WATER_3D_RAMP.splice(0, WATER_3D_RAMP.length, ...(wire.water_3d?.ramp ?? []))
      WATER_3D.aboveTop = wire.water_3d?.above_top ?? ''
    })
  return loading
}

/** `#RRGGBBAA` -> `rgba()`, so CSS and deck.gl read the same alpha the PNGs use. */
export function cssColour(colour: string): string {
  const hex = colour.replace('#', '')
  if (hex.length !== 8) return colour
  const n = (i: number) => parseInt(hex.slice(i, i + 2), 16)
  return `rgba(${n(0)}, ${n(2)}, ${n(4)}, ${(n(6) / 255).toFixed(3)})`
}

/** RGBA 0-255 of a ramp at a value, interpolated exactly as colormaps.sample_ramp does. */
export function rampRgba(stops: RampStop[], value: number, above?: string): [number, number, number, number] {
  const parse = (c: string): [number, number, number, number] => {
    const h = c.replace('#', '')
    const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) : 255
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16), a]
  }
  if (!stops.length || !Number.isFinite(value)) return [0, 0, 0, 0]
  if (above && value > stops[stops.length - 1][0]) return parse(above)
  if (value <= stops[0][0]) return parse(stops[0][1])
  for (let i = 1; i < stops.length; i += 1) {
    if (value <= stops[i][0]) {
      const [v0, c0] = stops[i - 1]
      const [v1, c1] = stops[i]
      const t = (value - v0) / (v1 - v0 || 1)
      const a = parse(c0)
      const b = parse(c1)
      return [0, 1, 2, 3].map((k) => Math.round(a[k] + (b[k] - a[k]) * t)) as [number, number, number, number]
    }
  }
  return parse(stops[stops.length - 1][1])
}
